<script setup lang="ts">
import type { ApalumaChatMessage, ChatMessageFeedback } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import { useAnalytics } from '@/composables/analytics/analytics-service';

const props = defineProps<{
   message: ApalumaChatMessage;
   isPositive: boolean;
   originalQuestion?: string;
}>();

const emit = defineEmits<{
   feedbackSubmitted: [feedback: ChatMessageFeedback];
}>();

const showFeedbackSection = defineModel<boolean>();
const { trackEvent } = useAnalytics();

const isInputOpen = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const error = ref<string>('');
const isSent = ref<boolean>(false);
const selectedFeedback = ref<string>('');

const scrollTarget = useTemplateRef('scrollTarget');

const positiveFeedback = [
   'Helpful',
   'Clear',
   'Accurate',
   'Informative',
   'Well-formatted',
];

const preDefinedFeedback = computed(() => {
   return positiveFeedback;
});

const feedbackInput = ref<string>('');

/**
 * Handles selecting a predefined feedback option
 * For positive feedback: sends immediately
 * For negative feedback: just selects it (user can add better question before submitting)
 */
function handleSelectFeedback(feedback: string) {
   if (props.isPositive) {
      // For positive feedback, send immediately
      sendFeedback(feedback);
   }
   else {
      // For negative feedback, just select it
      selectedFeedback.value = feedback;
   }
}

/**
 * Sends the feedback to the server
 * @param feedback - The feedback to send
 */
async function sendFeedback(feedback: string) {
   error.value = '';

   const trimmedFeedback = feedback.trim();

   if (!trimmedFeedback.length)
      return;

   try {
      isLoading.value = true;

      const { stopped, isInitializedDone, error: _error, feedback: _feedback, ...assistantMessageData } = props.message;

      trackEvent('chat:feedback', {
         message_id: props.message.id,
         thread_id: props.message.thread_id,
         sentiment: props.isPositive ? 'positive' : 'negative',
         bot_response: JSON.stringify(assistantMessageData),
         feedback: trimmedFeedback,
         is_positive: props.isPositive,
         question: props.originalQuestion || props.message.refined_question || '',
      });

      // Emit feedback submitted event to update the message feedback state
      emit('feedbackSubmitted', {
         isPositive: props.isPositive,
         feedback: trimmedFeedback,
         submittedAt: new Date().toISOString(),
      });

      isSent.value = true;
      feedbackInput.value = '';
      selectedFeedback.value = '';
      showFeedbackSection.value = false;
   }
   catch (e) {
      console.error('Error sending feedback:', e);
      error.value = 'Failed to send feedback';
   }
   finally {
      isLoading.value = false;
   }
}

/**
 * Handles the click event for toggling the feedback input state.
 */
async function handleClickOtherFeedback() {
   isInputOpen.value = !isInputOpen.value;
   if (isInputOpen.value) {
      await nextTick();
      scrollToBottom();
   }
}

/**
 * Scrolls to bottom of feedback
 */
function scrollToBottom() {
   scrollTarget.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
   });
}

onMounted(async () => {
   await nextTick();
   scrollToBottom();
});
</script>

<template>
   <div v-if="showFeedbackSection">
      <div class="relative w-full border border-black rounded-2xl px-3 pt-2.5 pb-3 overflow-visible">
         <!-- region: close button -->
         <button
            class="absolute top-2 right-2 p-1 hover:bg-white rounded-full"
            aria-label="Close feedback"
            @click="showFeedbackSection = false"
         >
            <Icon
               :icon="IconLib.actions.close"
               class="size-4 text-black"
            />
         </button>
         <!-- endregion: close button -->

         <!-- region: loading -->
         <div v-if="isLoading" class="absolute flex items-center justify-center inset-0 bg-neutral opacity-70">
            <Icon
               :icon="IconLib.loading.spinner"
               class="size-7 text-black/90"
            />
         </div>
         <!-- endregion: loading -->

         <p class="text-base font-semibold">
            {{ isPositive ? 'What did you like about this response?' : 'What didn\'t you like about this response?' }}
         </p>

         <!-- Positive feedback: show button options -->
         <template v-if="isPositive">
            <div class="grid grid-cols-3 gap-2 mt-3">
               <button
                  v-for="(feedback, index) in preDefinedFeedback"
                  :key="index"
                  class="button-feedback"
                  :class="{ 'button-feedback-selected': selectedFeedback === feedback }"
                  @click="handleSelectFeedback(feedback)"
               >
                  {{ feedback }}
               </button>

               <button
                  class="button-feedback"
                  :class="{ 'button-feedback-selected': isInputOpen }"
                  @click="handleClickOtherFeedback"
               >
                  Other...
               </button>
            </div>

            <!-- Other feedback input -->
            <div
               v-if="isInputOpen"
               class="w-full bg-white flex gap-1 outline outline-1 outline-black focus-within:outline-[3px]
            rounded-full p-2 mt-3"
            >
               <input
                  v-model="feedbackInput"
                  class="flex-1 ps-3 focus:outline-none placeholder:text-black/20"
                  placeholder="Add details here (optional)"
                  type="text"
                  aria-label="Add feedback"
                  @keydown.enter="sendFeedback(feedbackInput)"
               >

               <button
                  class="p-1 hover:bg-neutral rounded-full"
                  aria-label="Send feedback"
                  @click="sendFeedback(feedbackInput)"
               >
                  <Icon
                     :icon="IconLib.chat.send"
                     class="size-4 text-black/40"
                  />
               </button>
            </div>
         </template>

         <!-- Negative feedback: show only textbox -->
         <div v-else class="mt-3">
            <label class="text-sm text-black/70 block mb-2">
               What would be a better answer?
            </label>
            <div
               class="w-full bg-white flex gap-1 outline outline-1 outline-black/50 focus-within:outline-black focus-within:outline-2
               rounded-xl p-2"
            >
               <textarea
                  v-model="feedbackInput"
                  class="flex-1 ps-2 focus:outline-none placeholder:text-black/30 text-sm resize-none"
                  placeholder="Suggest a better answer..."
                  rows="3"
                  aria-label="Suggest a better answer"
               />
            </div>

            <!-- Submit button for negative feedback -->
            <button
               class="mt-3 px-4 py-2 bg-primary text-white rounded-full text-sm font-medium
               hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
               :disabled="!feedbackInput.trim()"
               @click="sendFeedback(feedbackInput)"
            >
               Submit Feedback
            </button>
         </div>

         <!-- region: error -->
         <span v-if="error" class="text-sm text-red-500 mt-3">{{ error }}</span>
      <!-- endregion: error -->
      </div>
      <!-- region: scroll target -->
      <div ref="scrollTarget"></div>
   <!-- endregion: scroll target -->
   </div>
</template>

<style scoped lang="postcss">
.button-feedback{
   @apply px-3 py-1.5 text-sm hover:bg-primary rounded-full hover:text-white;
   @apply flex items-center justify-center whitespace-nowrap;
   @apply border border-black hover:border-transparent;
   @apply transition-all duration-200;
}

.button-feedback-selected {
   @apply bg-primary text-white border-transparent;
}
</style>
